/**
 * Evidence API Client
 * Provides type-safe API calls for incident evidence (files from forms/workflows)
 *
 * Evidence vs Notes Attachments:
 * - Evidence: Files uploaded via forms/workflows, stored in inc_evidence table
 * - Notes Attachments: Files attached to notes, stored in inc_note_attachment table
 */

import { apiRequest } from './standard-api';

// ============================================
// TYPES
// ============================================

export interface Evidence {
  id: string;
  incidentId: string;
  noteId: string | null;
  blobPath: string;
  fileName: string;
  mime: string | null;
  sizeBytes: number | null;
  sha256: string | null;
  avStatus: 'pending' | 'clean' | 'infected' | 'error';
  redacted: boolean;
  createdAt: string;
}

export interface CreateEvidencePayload {
  blobPath: string;
  fileName: string;
  mime?: string;
  sizeBytes?: number;
  sha256?: string;
  noteId?: string;
}

export interface EvidenceDownloadResponse {
  downloadUrl: string;
  fileName: string;
  mimeType: string;
  expiresAt: string;
}

// ============================================
// EVIDENCE API
// ============================================

/**
 * List all evidence for an incident
 */
export async function listEvidence(incidentId: string): Promise<Evidence[]> {
  const response = await apiRequest<Evidence[]>(
    `/api/v1/incidents/${incidentId}/evidence`,
    { method: 'GET' }
  );
  return response.data || [];
}

/**
 * Add evidence to an incident
 */
export async function addEvidence(
  incidentId: string,
  payload: CreateEvidencePayload
): Promise<Evidence> {
  const response = await apiRequest<Evidence>(
    `/api/v1/incidents/${incidentId}/evidence`,
    {
      method: 'POST',
      body: JSON.stringify(payload)
    }
  );
  return response.data;
}

/**
 * Download evidence file
 * Uses the document download endpoint
 */
export async function downloadEvidence(
  incidentId: string,
  evidenceId: string
): Promise<EvidenceDownloadResponse> {
  const response = await apiRequest<EvidenceDownloadResponse>(
    `/api/v1/incidents/${incidentId}/evidence/${evidenceId}/download`,
    { method: 'GET' }
  );
  return response.data;
}

/**
 * Generate download URL for evidence (using blob path directly)
 */
export async function getEvidenceDownloadUrl(blobPath: string): Promise<string> {
  const response = await apiRequest<{ downloadUrl: string }>(
    `/api/v1/documents/download-url`,
    {
      method: 'POST',
      body: JSON.stringify({ blobPath })
    }
  );
  return response.data.downloadUrl;
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number | null): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Get file icon type based on MIME type
 */
export function getFileIconType(mimeType?: string | null): 'image' | 'pdf' | 'document' | 'file' {
  if (!mimeType) return 'file';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
  return 'file';
}

/**
 * Check if file is previewable
 */
export function isPreviewable(mimeType?: string | null): boolean {
  if (!mimeType) return false;
  return mimeType.startsWith('image/') || mimeType === 'application/pdf';
}

/**
 * Trigger file download
 */
export async function triggerEvidenceDownload(url: string, fileName: string): Promise<void> {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
